import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api/api";

export default function Questions() {
  const location = useLocation();
  const { link_hash } = location.state || {};
  const [questionData, setQuestionData] = useState(null);
  const [selectedOption, setSelectedOption] = useState([]);
  const [message, setMessage] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [timeLeft, setTimeLeft] = useState(() => {
    const savedTime = sessionStorage.getItem("timeLeft");
    return savedTime ? parseInt(savedTime, 10) : 0;
  });
  const [initialTime, setInitialTime] = useState(() => {
    const savedInitialTime = sessionStorage.getItem("initialTime");
    return savedInitialTime ? parseInt(savedInitialTime, 10) : 0;
  });
  const navigate = useNavigate();
  const [isTabMinimized, setIsTabMinimized] = useState(false);

  const handleTabMinimize = async () => {
    try {
      const response = await api.post(
        `/assessment/screen_min_count/?link_hash=${link_hash}`
      );
      if (response) {
        console.log("API called successfully");
      }
    } catch (error) {
      console.log("Error in handleTabMinimize:", error);
    }
  };

  // Handle tab visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsTabMinimized(true);
        handleTabMinimize();
      } else {
        setIsTabMinimized(false);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Timer management
  useEffect(() => {
    let timer;

    if (timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => {
          const newTime = prevTime - 1;
          sessionStorage.setItem("timeLeft", newTime);
          if (newTime <= 0) {
            sessionStorage.clear();
            navigate("/thankyou");
            return 0;
          }
          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [timeLeft, navigate]);

  const formatTime = useCallback((seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }, []);

  const fetchQuestion = useCallback(async () => {
    try {
      const response = await api.get("/assessment/get_question/", {
        params: { link_hash },
      });

      if (response.data.data) {
        setQuestionData(response.data.data);

        // Initialize timer only once
        if (initialTime === 0) {
          const newTime = response?.data?.assessment_time_minutes * 60;
          setInitialTime(newTime);
          sessionStorage.setItem("initialTime", newTime);

          // Only set timeLeft if it's not already set
          if (timeLeft <= 0) {
            setTimeLeft(newTime);
            sessionStorage.setItem("timeLeft", newTime);
          }
        }

        setSelectedOption([]);
      } else {
        setMessage("All questions have been attempted.");
        sessionStorage.clear();
        navigate("/thankyou");
      }
      setIsFetching(false);
    } catch (error) {
      setIsFetching(false);
      console.error("Error fetching question data:", error);
      setMessage(
        error.response?.data.message || "Error fetching question data"
      );
    }
  }, [link_hash, navigate, initialTime, timeLeft]);

  // Initial question fetch
  useEffect(() => {
    if (link_hash) {
      fetchQuestion();
    } else {
      setMessage("Link hash is missing. Cannot fetch questions.");
    }
  }, [link_hash]);

  const handleOptionChange = (optionId) => {
    setSelectedOption((prevSelectedOptions) => {
      if (prevSelectedOptions.includes(optionId)) {
        return prevSelectedOptions.filter((id) => id !== optionId);
      } else {
        return [...prevSelectedOptions, optionId];
      }
    });
  };

  const handleSaveAnswer = async () => {
    if (selectedOption?.length === 0) {
      setMessage("Please select an option before next question.");
      return;
    }

    const payload = {
      link_hash: link_hash,
      selected_choices: selectedOption,
    };

    try {
      setIsFetching(true);
      await api.post("/assessment/capture_choice/", payload);
      fetchQuestion();
    } catch (error) {
      setIsFetching(false);
      console.error(
        "Error saving answer:",
        error.response ? error.response.data : error
      );
      setMessage(error.response?.data.message || "Error saving answer");
    }
  };

  const handleNextQuestion = () => {
    if (selectedOption?.length === 0) {
      setMessage("Please select an option before next question.");
    } else {
      setMessage("");
      handleSaveAnswer();
    }
  };

  const generateLabel = (index) => {
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    return letters[index] || String(index + 1);
  };

  // Full screen handling
  useEffect(() => {
    const enableFullScreen = async () => {
      try {
        if (!document.fullscreenElement) {
          await document.documentElement.requestFullscreen();
        }
      } catch (err) {
        console.warn("Failed to enter full screen:", err);
        setMessage(
          "Please press F11 to enter full screen mode for better experience."
        );
      }
    };

    enableFullScreen();

    return () => {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch((err) => console.warn(err));
      }
    };
  }, []);

  if (!questionData)
    return (
      <div className="d-flex justify-content-center align-items-center full-height-background">
        <span
          className="spinner-border spinner-border-sm me-2"
          role="status"
          aria-hidden="true"
        ></span>
        <span className="sr-only text">Loading question...</span>
      </div>
    );

  const getQuestion = () => {
    if (questionData?.text) {
      return `${questionData?.qn_no}. ${questionData?.text}`;
    } else {
      return "No question text available.";
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-center align-items-center full-height-background">
        <div className="container justify-content-center align-items-center">
          <div className="timer mt-3 text-center mb-3">
            <h5>Time Left: {formatTime(timeLeft)}</h5>
          </div>
          <div className="questionery-wrapper bg-white">
            <div className="question-text-main p-4">
              <div className="question-text align-items-center d-flex gap-3 mb-2">
                <h2>{getQuestion()}</h2>
              </div>
              <div className="answer-text fw-medium">
                Choose the correct answer.
              </div>
            </div>
            <div className="answer-box p-4">
              <div className="d-flex flex-column gap-3">
                {questionData?.choices && questionData?.choices.length > 0 ? (
                  questionData?.choices.map((choice, index) => (
                    <div
                      key={choice.id}
                      className="col-12 form-check d-flex gap-3 align-items-center position-relative"
                    >
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="option"
                        id={`option${index + 1}`}
                        value={choice.id}
                        checked={selectedOption.includes(choice.id)}
                        onChange={() => handleOptionChange(choice.id)}
                      />
                      <div
                        onClick={() => handleOptionChange(choice.id)}
                        className="form-check-div"
                      >
                        {generateLabel(index)}
                      </div>
                      <label className="form-check-label">{choice.text}</label>
                    </div>
                  ))
                ) : (
                  <p>No choices available.</p>
                )}
              </div>
            </div>
          </div>
          <div className="questionery-button d-flex justify-content-end mt-4">
            <button
              className="btn btn-prev"
              onClick={handleNextQuestion}
              disabled={isFetching}
            >
              {isFetching ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Loading...
                </>
              ) : (
                "Next"
              )}
            </button>
          </div>
          {message && <div className="alert alert-info mt-3">{message}</div>}
        </div>
      </div>
    </div>
  );
}
